import {
  ConfirmModalComponent,
  EmptyPageComponent,
  GroupAssessmentsTableComponent,
  PagesHeaderComponent,
  PaginationComponent,
  SynDialogConfig,
} from '$synapse/core';
import { Group, GroupAssessment, Page } from '$synapse/models';
import { GroupService } from '$synapse/services';
import {
  Component,
  computed,
  OnInit,
  Signal,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map, Observable, firstValueFrom } from 'rxjs';
import { GroupAssessmentCreationModalComponent } from '../group-assessment-creation-modal/group-assessment-creation-modal.component';
import { GroupAddCandidatesComponent } from '../../candidates/group-add-candidates/group-add-candidates.component';
import { CandidateSummary } from '$synapse/dtos';
import { CandidateImportComponent } from '../group-candidate-import-modal/candidate-import.component';
import { MatDialog } from '@angular/material/dialog';
import { GroupMetadataWidgetComponent } from '../group-description-card/group-description-card.component';
import { GroupCandidatesTableComponent } from '../../../core/components/group-candidate-table/group-candidates-table.component';
import { SHARED_IMPORTS } from '../../../core/shared-imports';

@Component({
  selector: 'syn-group-details',
  templateUrl: './group-details.component.html',
  styleUrls: ['./group-details.component.scss'],
  imports: [
    SHARED_IMPORTS,
    PagesHeaderComponent,
    GroupMetadataWidgetComponent,
    EmptyPageComponent,
    GroupAssessmentsTableComponent,
    PaginationComponent,
    GroupCandidatesTableComponent,
  ],
})
export class GroupsDetailsComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _groupService = inject(GroupService);
  private readonly _modalDialog = inject(MatDialog);

  group: Group = {} as Group;

  readonly candidates$: Observable<Page<CandidateSummary>> =
    this._route.data.pipe(map((data) => data['candidates']));
  private _candidates: CandidateSummary[];

  readonly groupAssessments$: Observable<Page<GroupAssessment>> =
    this._route.data.pipe(map((data) => data['assessments']));

  groupAssessmentsSignal: WritableSignal<Page<GroupAssessment>> =
    signal<Page<GroupAssessment>>(undefined);

  get groupAssessments(): Signal<Page<GroupAssessment>> {
    return this.groupAssessmentsSignal;
  }

  hasAssessment = computed(
    () => this.groupAssessmentsSignal().content.length > 0
  );

  private _emailCount: number;

  ngOnInit(): void {
    this.candidates$.subscribe((data) => {
      this._candidates = data.content;
    });

    if (history.state.group) {
      this.group = new Group(history.state.group);
    } else {
      this.refreshGroup();
    }

    this.groupAssessments$.subscribe((groupAssessment) =>
      this.groupAssessmentsSignal.set(groupAssessment)
    );
  }

  private refreshGroup() {
    this._groupService
      .getGroup(this._route.snapshot.params.id)
      .subscribe((data) => {
        this.group = data;
      });
  }

  addCandidatesToGroup() {
    const dialogRef = this._modalDialog.open(
      GroupAddCandidatesComponent,
      new SynDialogConfig(
        'group-add-candidate',
        this.group,
        SynDialogConfig.Size.LG
      )
    );

    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (isSuccess) {
        this._router.navigate([location.pathname], {
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  async count(): Promise<number> {
    const groupId = this._route.snapshot.params.id;
    return await firstValueFrom(
      this._groupService.countReminderEmails(groupId)
    );
  }

  async sendEmailRemindCandidatesOfGroup() {
    this._emailCount = await this.count();
    const dialogRef = this._modalDialog.open(
      ConfirmModalComponent,
      new SynDialogConfig('send-email-confirmation')
    );
    const instance = dialogRef.componentInstance;
    instance.title = `Envoi d'email de relance aux candidats du groupe ${this.group.name}`;
    instance.message = 'Êtes-vous sûr de vouloir envoyer ces mails ?';
    instance.action = 'Envoyer';
    instance.warn = `Attention ! Vous allez envoyer ${this._emailCount} mail(s)`;
    instance.confirmationEvent.subscribe(() => {
      this._groupService
        .sendEmailRemindCandidatesOfGroup(this.group.id)
        .subscribe();
    });
  }

  importCandidate(groupId: string) {
    const dialogRef = this._modalDialog.open(
      CandidateImportComponent,
      new SynDialogConfig('import-modal-component', groupId)
    );
    dialogRef.afterClosed().subscribe(() => {
      this.reloadPage();
    });
  }

  assignAssessment() {
    if (this._candidates.length === 0) {
      return;
    }
    const dialogRef = this._modalDialog.open(
      GroupAssessmentCreationModalComponent,
      new SynDialogConfig('modal-assessment-component')
    );
    const instance = dialogRef.componentInstance;
    instance.addAssessment.subscribe((assessment) => {
      const confirmDialogRef = this._modalDialog.open(
        ConfirmModalComponent,
        new SynDialogConfig('assessment-confirmation')
      );
      const confirmInstance = confirmDialogRef.componentInstance;
      confirmInstance.title = "Confirmation d'assignation d'évaluation";
      confirmInstance.message =
        'Des mails de notification seront envoyés aux candidats. Êtes-vous sûr de confirmer ?';
      confirmInstance.action = 'Confirmer et envoyer';
      confirmInstance.buttonColor = 'primary';
      confirmInstance.confirmationEvent.subscribe(() => {
        this._groupService
          .assignAssessmentToGroup(this.group, assessment)
          .subscribe(() => {
            this.reloadPage();
          });
      });
    });
  }

  private reloadPage() {
    this._router.navigate(['/groups/' + this._route.snapshot.params['id']], {
      queryParamsHandling: 'merge',
    });
  }
}
